import { useState, useEffect, type FC } from 'react';
import { Button, Spin, Empty } from 'antd';
import { Link } from 'react-router-dom';
import {
  AimOutlined,
  BarChartOutlined,
  BulbOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { StartInterviewFlowModal } from '@/components/StartInterviewFlowModal';
import { InterviewPositionCode } from '@/types';
import { getExperienceList } from '@/services/experience';
import type { CExperienceItemDTO } from '@/types/experience';

type Feature = {
  icon: typeof AimOutlined;
  title: string;
  description: string;
  highlight?: boolean;
};

// 热点专题 - 岗位数据
type HotPosition = {
  id: string;
  name: string;
  code?: InterviewPositionCode;
};

const hotPositions: HotPosition[] = [
  { id: '1', code: 'AI_AGENT_ENGINEER', name: 'AI Agent 工程师' },
  { id: '2', name: 'Prompt 工程师' },
  { id: '3', name: 'LLM 应用开发' },
  { id: '4', name: 'AI 产品经理' },
  { id: '5', name: '大模型算法工程师' },
];

const features: Feature[] = [
  {
    icon: AimOutlined,
    title: '智能提问',
    description: 'AI 根据你的简历智能生成面试问题，精准匹配岗位要求',
    highlight: true,
  },
  {
    icon: BulbOutlined,
    title: '实时追问',
    description: '根据你的回答深度追问，模拟真实面试场景',
  },
  {
    icon: BarChartOutlined,
    title: '专业报告',
    description: '面试结束后生成详细分析报告，帮助你了解自己的优势与不足',
  },
  {
    icon: ThunderboltOutlined,
    title: '即时反馈',
    description: '实时评估答题质量，给出改进建议',
  },
];

const Home: FC = () => {
  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const [defaultPositionCode, setDefaultPositionCode] = useState<InterviewPositionCode>();
  const [featuredGuides, setFeaturedGuides] = useState<CExperienceItemDTO[]>([]);
  const [loadingGuides, setLoadingGuides] = useState(true);
  const [guidesError, setGuidesError] = useState(false);

  // 加载精选面经数据（取前3条）
  useEffect(() => {
    const loadFeaturedGuides = async () => {
      setLoadingGuides(true);
      setGuidesError(false);
      try {
        const data = await getExperienceList({ page: 1, pageSize: 3 });
        setFeaturedGuides(data.items);
      } catch {
        setGuidesError(true);
      } finally {
        setLoadingGuides(false);
      }
    };
    loadFeaturedGuides();
  }, []);

  const handleStartInterview = () => {
    setDefaultPositionCode(undefined);
    setFlowModalOpen(true);
  };

  return (
    <div className="w-full bg-slate-50 dark:bg-slate-950">
      {/* Hero 区域 - 紧凑设计，首屏展示更多专题内容 */}
      <section className="relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -right-20 -top-20 h-[160px] w-[160px] rotate-6 bg-indigo-600/80 dark:bg-indigo-700 sm:-right-16 sm:-top-16 sm:h-[280px] sm:w-[280px] sm:bg-indigo-600" />
          <div className="absolute -right-10 -top-10 h-[130px] w-[130px] rotate-12 bg-indigo-500/80 dark:bg-indigo-600 sm:-right-8 sm:-top-8 sm:h-[220px] sm:w-[220px] sm:bg-indigo-500" />
        </div>

        <div className="relative mx-auto max-w-6xl px-6 py-10 sm:py-14 lg:py-16">
          <div className="max-w-2xl">
            {/* 标签 */}
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-3 py-1 text-sm font-medium text-white sm:mb-5">
              <RocketOutlined className="text-sm" />
              AI 面试助手
            </div>

            {/* 主标题 */}
            <h1 className="text-3xl font-black leading-[1.1] tracking-tight text-slate-900 dark:text-white sm:text-4xl lg:text-5xl">
              练好面试，<span className="text-indigo-600 dark:text-indigo-400">拿下 offer</span>
            </h1>

            {/* 副标题 */}
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-600 dark:text-slate-300 sm:mt-4 sm:text-base lg:text-lg">
              上传简历，开启智能面试体验。AI 精准提问，专业反馈，助你脱颖而出。
            </p>

            {/* CTA 按钮 */}
            <div className="mt-5 flex flex-wrap gap-3 sm:mt-6">
              <Button
                type="primary"
                size="large"
                onClick={handleStartInterview}
                icon={<UploadOutlined />}
                className="min-h-[44px]"
              >
                立即开始
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 专题区域 - AI Agent 工程师面试 */}
      <section className="relative overflow-hidden bg-slate-950 py-10 sm:py-12 lg:py-14">
        {/* 背景装饰 */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute right-0 top-0 h-full w-1/2 -skew-x-12 bg-gradient-to-l from-indigo-600/10 to-transparent" />
          <div className="absolute -left-20 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full bg-indigo-600/10 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-6xl px-6">
          {/* 标题区域 */}
          <div className="mb-6 sm:mb-8 lg:mb-10">
            <div className="mb-2 inline-flex items-center gap-2 border border-indigo-500/30 bg-indigo-500/10 px-3 py-1 text-xs font-medium text-indigo-300 sm:text-sm">
              <ThunderboltOutlined className="text-sm" />
              <span>HOT TOPIC</span>
            </div>
            <h2 className="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">
              AI Agent 工程师{' '}
              <span className="bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                面试专题
              </span>
            </h2>
            <p className="mt-2 max-w-lg text-sm text-slate-400">
              AI Agent 岗位需求井喷，<span className="text-white">精选面经</span> +{' '}
              <span className="text-white">模拟训练</span>，助你斩获高薪 Offer
            </p>
          </div>

          {/* 不对称布局：面经为主 + 侧边栏 */}
          <div className="grid gap-5 lg:grid-cols-12 lg:gap-6">
            {/* 左侧：精选面试经验（主要内容区） */}
            <div className="space-y-4 lg:col-span-7">
              {loadingGuides ? (
                <div className="flex justify-center py-12">
                  <Spin size="large" />
                </div>
              ) : guidesError ? (
                <div className="flex justify-center py-12">
                  <Empty description="加载失败，请稍后重试" />
                </div>
              ) : featuredGuides.length === 0 ? (
                <div className="flex justify-center py-12">
                  <Empty description="暂无面经" />
                </div>
              ) : (
                featuredGuides.map((guide, index) => (
                  <Link
                    key={guide.id}
                    to={`/interview-experience/${guide.experienceNumber}`}
                    className="group relative block overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/80 p-5 backdrop-blur transition-all hover:border-indigo-500/50 hover:bg-slate-900 sm:p-6"
                  >
                    {/* 大号序号装饰 */}
                    <div className="absolute -right-2 -top-3 text-8xl font-black text-slate-800/30 transition-colors group-hover:text-indigo-600/20">
                      {index + 1}
                    </div>

                    <div className="relative">
                      <div className="flex items-start gap-4">
                        {/* 序号徽章 */}
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 text-lg font-bold text-indigo-400 transition-colors group-hover:bg-indigo-500/20 sm:h-14 sm:w-14 sm:text-xl">
                          {index + 1}
                        </div>

                        <div className="min-w-0 flex-1">
                          <h4 className="text-base font-semibold text-white transition-colors group-hover:text-indigo-300 sm:text-lg">
                            {guide.title}
                          </h4>
                          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-400 sm:mt-3 sm:text-base">
                            {guide.summary || '暂无摘要'}
                          </p>
                          <div className="mt-3 flex flex-wrap items-center gap-2 sm:mt-4">
                            {guide.tags && guide.tags.length > 0 ? (
                              guide.tags.slice(0, 3).map((tag) => (
                                <span
                                  key={tag}
                                  className="rounded-full bg-slate-800 px-2.5 py-1 text-xs text-slate-400 transition-colors group-hover:bg-indigo-900/50 group-hover:text-indigo-300 sm:text-sm"
                                >
                                  {tag}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-slate-500">暂无标签</span>
                            )}
                            <span className="ml-auto flex items-center gap-1 text-xs font-medium text-indigo-400 sm:text-sm">
                              查看详情{' '}
                              <RightOutlined className="transition-transform group-hover:translate-x-1" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              )}

              {/* 更多文章入口 */}
              <Link
                to="/interview-experience"
                className="group flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-700 bg-slate-800/30 py-2 text-sm text-slate-400 transition-all hover:border-indigo-500/50 hover:bg-slate-800 hover:text-white sm:text-base"
              >
                <span>查看更多面经</span>
                <RightOutlined className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            {/* 右侧：快捷入口 + 热门职位 */}
            <div className="space-y-5 lg:col-span-5">
              {/* 开启面试 CTA */}
              <div className="relative overflow-hidden rounded-2xl border border-indigo-500/20 bg-gradient-to-br from-indigo-600 via-indigo-700 to-slate-900 p-5 text-white sm:p-6">
                <div className="absolute -right-10 -top-10 h-40 w-40 rotate-12 border-2 border-white/10" />
                <div className="absolute -bottom-8 -left-8 h-24 w-24 rotate-45 border border-white/5" />

                <div className="relative">
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 backdrop-blur sm:h-14 sm:w-14">
                    <RocketOutlined className="text-2xl text-white" />
                  </div>

                  <h3 className="text-xl font-bold sm:text-2xl">开启模拟面试</h3>
                  <p className="mt-2 text-sm text-indigo-200 sm:text-base">
                    选择 AI Agent 岗位，立即开始实战训练
                  </p>

                  <Button
                    type="default"
                    size="large"
                    className="mt-5 min-h-[44px] border-0 bg-white px-8 text-base font-semibold text-indigo-600 shadow-lg shadow-black/20 hover:bg-indigo-50 sm:mt-6"
                    onClick={() => {
                      setDefaultPositionCode('AI_AGENT_ENGINEER');
                      setFlowModalOpen(true);
                    }}
                    icon={<ThunderboltOutlined />}
                  >
                    立即开始
                  </Button>
                </div>
              </div>

              {/* 热门职位 */}
              <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 backdrop-blur sm:p-6">
                <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-white sm:text-lg">
                  <span className="inline-block h-2 w-2 rounded-full bg-amber-400" />
                  热门职位
                </h3>
                <div className="space-y-2">
                  {hotPositions.map((position) => {
                    const isActive = position.id === '1';
                    return (
                      <div
                        key={position.id}
                        className={`flex items-center justify-between rounded-xl px-4 py-3 transition-colors ${
                          isActive
                            ? 'cursor-pointer border border-indigo-500/30 bg-indigo-500/10 hover:border-indigo-500/50 hover:bg-indigo-500/20'
                            : 'border border-transparent bg-slate-800/50'
                        }`}
                        onClick={
                          isActive
                            ? () => {
                                setDefaultPositionCode(position.code);
                                setFlowModalOpen(true);
                              }
                            : undefined
                        }
                        role={isActive ? 'button' : undefined}
                        tabIndex={isActive ? 0 : undefined}
                      >
                        <span
                          className={`text-sm font-medium ${isActive ? 'text-white' : 'text-slate-500'}`}
                        >
                          {position.name}
                        </span>
                        {isActive ? (
                          <span className="rounded-full bg-amber-500/20 px-2.5 py-1 text-xs font-medium text-amber-400">
                            可面试
                          </span>
                        ) : (
                          <span className="text-xs text-slate-600">暂未开启</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 特性区域 - 不对称布局，一个突出的卡片 */}
      <section id="features-section" className="bg-white py-16 dark:bg-slate-900 lg:py-28">
        <div className="mx-auto max-w-6xl px-6">
          {/* 左对齐标题 */}
          <div className="mb-10 max-w-xl lg:mb-12">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl lg:text-4xl">
              为什么选择小灵
            </h2>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 sm:mt-4 sm:text-base">
              基于先进的 AI 技术，为你提供最接近真实的面试体验
            </p>
          </div>

          {/* 不对称卡片布局 - 一个大的 + 三个小的 */}
          <div className="grid gap-4 sm:gap-6 lg:grid-cols-2 lg:grid-rows-2">
            {/* 突出的主卡片 */}
            <div className="group relative overflow-hidden rounded-3xl bg-indigo-600 p-6 text-white transition-colors hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-600 sm:p-8 lg:row-span-2 lg:p-12">
              {/* 装饰元素 */}
              <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full border-8 border-white/10" />
              <div className="absolute -bottom-12 -right-12 h-48 w-48 rounded-full border-8 border-white/5" />

              <div className="relative">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 sm:mb-6 sm:h-16 sm:w-16">
                  <AimOutlined className="text-xl sm:text-3xl" />
                </div>
                <h3 className="text-xl font-bold sm:text-2xl lg:text-3xl">智能提问</h3>
                <p className="mt-3 max-w-sm text-base leading-relaxed text-white/90 sm:mt-4 sm:text-lg">
                  AI
                  根据你的简历智能生成面试问题，精准匹配岗位要求。每一次提问都经过精心设计，帮助你在真实面试中游刃有余。
                </p>
              </div>
            </div>

            {/* 三个次要卡片 */}
            {features.slice(1).map((feature) => (
              <div
                key={feature.title}
                className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 transition-colors hover:border-indigo-200 hover:bg-white hover:shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:hover:border-indigo-500 dark:hover:bg-slate-750 sm:gap-5 sm:p-6"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400 sm:h-12 sm:w-12">
                  <feature.icon className="text-lg sm:text-xl" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white sm:text-lg">
                    {feature.title}
                  </h3>
                  <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-400 sm:text-sm">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 流程区域 - 时间线样式 */}
      <section className="bg-slate-900 py-16 dark:bg-slate-950 lg:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-10 text-center lg:mb-12">
            <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              简单四步，开启面试之旅
            </h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-slate-400 sm:mt-4 sm:text-base">
              从上传简历到获取报告，整个过程简单直观
            </p>
          </div>

          {/* 水平时间线 */}
          <div className="relative">
            {/* 连接线 */}
            <div className="absolute top-6 left-0 right-0 hidden h-0.5 bg-slate-700 md:block" />

            <div className="relative grid gap-6 sm:grid-cols-2 sm:gap-8 md:grid-cols-4">
              {[
                { num: '1', title: '上传简历', desc: '支持 PDF、Word' },
                { num: '2', title: '选择岗位', desc: '匹配目标职位' },
                { num: '3', title: '开始面试', desc: 'AI 实时对话' },
                { num: '4', title: '获取报告', desc: '详细评估反馈' },
              ].map((step) => (
                <div key={step.num} className="relative text-center">
                  {/* 步骤圆圈 */}
                  <div className="relative z-10 mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-indigo-600 text-base font-bold text-white ring-4 ring-slate-900 sm:mb-4 sm:h-12 sm:w-12 sm:text-lg">
                    {step.num}
                  </div>
                  <h3 className="text-base font-semibold text-white sm:text-lg">{step.title}</h3>
                  <p className="mt-1 text-xs text-slate-400 sm:text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <StartInterviewFlowModal
        open={flowModalOpen}
        onCancel={() => setFlowModalOpen(false)}
        defaultPositionCode={defaultPositionCode}
      />
    </div>
  );
};

export default Home;
